import type { ColDef, IServerSideDatasource } from "ag-grid-community";
import dayjs from "dayjs";
import React from "react";

import { AgGridReact } from "@/components/grid/AgGrid";
import { Link } from "@/components/Link";
import "@/components/grid/AgGrid";

import { trpc } from "@blis/trpc/react";

import { useAdminServerSideGrid } from "@/features/admin/utils/useAdminServerSideGrid";
import type { ActionLog, AuditLog } from "../../types";

export function ActivityTable() {
  const dataSource = useDataSource();
  const { gridProps } = useAdminServerSideGrid<ActionLog>({
    serverSideDatasource: dataSource,
    paginationPageSize: 500,
    paginationPageSizeSelector: false,
  });
  const columns = useColumns();
  const detailsRowData = useDetailsRowData();

  return (
    <div className="ag-theme-quartz ag-theme-blis h-full w-full">
      <AgGridReact
        {...gridProps}
        masterDetail={true}
        columnDefs={columns}
        detailCellRendererParams={detailsRowData}
        sideBar={{
          toolPanels: ["filters"],
        }}
        defaultColDef={{
          resizable: true,
          sortable: false,
          flex: 1,
        }}
      />
    </div>
  );
}

function useColumns(): ColDef<ActionLog>[] {
  const utils = trpc.useUtils();
  return React.useMemo(() => {
    const columns: ColDef<ActionLog>[] = [
      {
        field: "txnTimestamp",
        filter: "agDateColumnFilter",
        filterParams: {
          filterOptions: ["equals", "lessThan", "greaterThan", "inRange"],
          maxNumConditions: 1,
        },
        headerName: "Timestamp",
        sortable: true,
        initialSort: "desc",
        valueFormatter: ({ value }) => dayjs(value).format("YYYY-MM-DD HH:mm"),
        cellRenderer: "agGroupCellRenderer",
      },
      {
        field: "action",
        filter: "agSetColumnFilter",
        filterParams: {
          values: async (params) => {
            try {
              const actions = await utils.admin.audit.actions.fetch();
              params.success(actions);
            } catch (_err) {
              params.fail();
            }
          },
        },
      },
      {
        field: "actor",
        filter: "agSetColumnFilter",
        filterParams: {
          values: async (params) => {
            try {
              const actors = await utils.admin.audit.actors.fetch();
              params.success(actors);
            } catch (_err) {
              params.fail();
            }
          },
        },
      },
      {
        field: "accessionId",
        headerName: "Accession",
        filter: "agTextColumnFilter",
        filterParams: {
          filterOptions: ["equals"],
          maxNumConditions: 1,
        },
        cellRenderer: ({ value }) => (
          <Link
            to="/accessions/$id"
            params={{ id: value }}
            target="_blank"
            rel="noopener noreferrer"
          >
            {value}
          </Link>
        ),
      },
      {
        field: "caseId",
        headerName: "Donor ID",
        filter: "agTextColumnFilter",
        filterParams: {
          filterOptions: ["equals"],
          maxNumConditions: 1,
        },
        initialHide: true,
      },
      {
        field: "unosId",
        headerName: "UNOS ID",
        filter: "agTextColumnFilter",
        filterParams: {
          filterOptions: ["equals"],
          maxNumConditions: 1,
        },
        initialHide: true,
      },
      {
        field: "txnUuid",
        headerName: "Transaction UUID",
        filter: "agTextColumnFilter",
        filterParams: {
          filterOptions: ["equals"],
          maxNumConditions: 1,
        },
        initialHide: true,
      },
    ];
    return columns;
  }, [utils.admin.audit.actions, utils.admin.audit.actors]);
}

function useDataSource(): IServerSideDatasource {
  const utils = trpc.useUtils();
  return React.useMemo(
    () => ({
      getRows: async (params) => {
        const { startRow, endRow, filterModel, sortModel } = params.request;
        try {
          const { data, totalCount } = await utils.admin.audit.list.fetch({
            take: (endRow ?? 0) - (startRow ?? 0),
            skip: startRow ?? 0,
            // filterModel,
            // biome-ignore lint/suspicious/noExplicitAny: TODO
            sortModel: sortModel as any,
            // biome-ignore lint/suspicious/noExplicitAny: TODO
            filterModel: filterModel as any,
          });
          params.success({
            rowData: data,
            rowCount: totalCount,
          });
        } catch (_err) {
          params.fail();
          return;
        }
      },
    }),
    [utils],
  );
}

function useDetailsRowData() {
  const utils = trpc.useUtils();

  const columnDefs = React.useMemo(() => {
    const columns: ColDef<AuditLog>[] = [
      { field: "action" },
      { field: "resource" },
      {
        field: "field",
      },
      {
        field: "oldValue",
        cellDataType: "text",
        cellRenderer: ({ value }) => (
          <span className="text-destructive">{value?.toString?.()}</span>
        ),
      },
      {
        field: "newValue",
        cellDataType: "text",
        cellRenderer: ({ value }) => (
          <span className="text-green-700">{value?.toString?.()}</span>
        ),
        // headerName: "Resource",
      },
    ];
    return columns;
  }, []);

  const getDetailRowData = React.useCallback(
    async (params) => {
      const details = await utils.admin.audit.details.fetch(
        params.data.txnUuid
          ? {
              uuid: params.data.txnUuid,
            }
          : {
              id: params.data.txnId,
            },
      );

      params.successCallback(details);
    },
    [utils.admin.audit.details],
  );

  return {
    detailGridOptions: {
      columnDefs,
      defaultColDef: { flex: 1, resizable: true, sortable: false },
    },
    getDetailRowData,
  };
}
